import {Component, inject} from '@angular/core';
import {DatePipe, NgIf} from "@angular/common";
import {FormControl, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatIcon} from "@angular/material/icon";
import {ButtonComponent} from "../../shared/button/button.component";
import {TranslateModule} from "@ngx-translate/core";
import {ButtonSize, ButtonState, ButtonType, ILanguage} from "../../../assets/types/dtoTypes";
import {LanguageDataService} from "../../shared/services/language-data.service";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {TranslateResultDialogComponent} from "./translate-progress-modal/translate-result-modal.component";
import {AgencyDashboardPageService} from "../../pages/main-pages/agency-dashboard-page/agency-dashboard-page.service";
import {finalize} from "rxjs";
import {ShowPopupService} from "../../shared/services/show-popup.service";
import {DropdownComponent} from "../../shared/countries-dropdown/dropdown.component";

@Component({
  selector: 'app-translate-document-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIcon,
    DatePipe,
    ButtonComponent,
    TranslateModule,
    NgIf,
    DropdownComponent
  ],
  templateUrl: './translate-document-modal.component.html',
  styleUrl: './translate-document-modal.component.scss'
})
export class TranslateDocumentModalComponent {
  fileName = 'document.pdf';
  uploadDate: Date | null = null;

  private dataService = inject(LanguageDataService);
  private agencyDashboardPageService = inject(AgencyDashboardPageService);
  private dialog = inject(MatDialog);
  private data = inject(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<TranslateDocumentModalComponent>);
  private popupService = inject(ShowPopupService);

  sourceLangCtrl = new FormControl<string | null>(null, { validators: [Validators.required] });
  targetLangCtrl = new FormControl<string | null>(null, { validators: [Validators.required] });

  isTranslating = false;

  languages: ILanguage[] = [];

  languageCountryMap: { [key: string]: string } = {};

  constructor() {
    if (this.data?.fileName) {
      this.fileName = String(this.data.fileName);
    }
    if (this.data?.uploadDate) {
      const parsed = new Date(this.data.uploadDate);
      this.uploadDate = isNaN(parsed.getTime()) ? null : parsed;
    }

    this.dataService.getLanguages().subscribe((returnData) => {
      this.languages = returnData.languages;
      this.languages.forEach(lang => {
        if (lang?.name && lang?.code) {
          this.languageCountryMap[lang.name] = lang.code;
        }
      });
    });
  }


  private getCode(selection: string | null | undefined): string | null {
    if (!selection) return null;
    const fromMap = this.languageCountryMap[selection];
    return (fromMap ?? selection).toLowerCase().trim();
  }
  private toDeepLSourceCode(code: string): string {
    const c = code.toLowerCase();
    if (c === 'en-gb' || c === 'en-us') return 'en';
    if (c === 'pt-pt' || c === 'pt-br') return 'pt';
    return c;
  }

  private toDeepLTargetCode(code: string): string {
    const c = code.toLowerCase();
    if (['en-gb', 'en-us', 'pt-pt', 'pt-br'].includes(c)) return c;
    return c;
  }

  private baseLang(code: string): string {
    const c = code.toLowerCase();
    if (c.startsWith('en')) return 'en';
    if (c.startsWith('pt')) return 'pt';
    return c;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onTranslate() {
    if (this.sourceLangCtrl.invalid || this.targetLangCtrl.invalid) return;

    const rawSourceCode = this.getCode(this.sourceLangCtrl.value);
    const rawTargetCode = this.getCode(this.targetLangCtrl.value);
    if (!rawSourceCode || !rawTargetCode) return;

    const sourceCode = this.toDeepLSourceCode(rawSourceCode);
    const targetCode = this.toDeepLTargetCode(rawTargetCode);

    if (this.baseLang(sourceCode) === this.baseLang(targetCode)) {
      this.dialog.open(TranslateResultDialogComponent, {
        data: { loading: false, success: false, message: 'Please choose different source and target languages.' },
        panelClass: 'no-padding-dialog',
        disableClose: true
      });
      return;
    }

    if (!this.data?.materialId) {
      this.dialog.open(TranslateResultDialogComponent, {
        data: { loading: false, success: false, message: 'Missing document identifier. Please reopen and try again.' },
        panelClass: 'no-padding-dialog',
        disableClose: true
      });
      return;
    }

    const resultRef = this.dialog.open(TranslateResultDialogComponent, {
      data: { loading: true, message: 'Translating your document…' },
      panelClass: 'no-padding-dialog',
      disableClose: true
    });

    this.isTranslating = true;

    this.agencyDashboardPageService.translateDocument(this.data.materialId, sourceCode, targetCode)
      .pipe(finalize(() => (this.isTranslating = false)))
      .subscribe({
        next: () => {
          resultRef.close();
          this.dialogRef.close({ refresh: true });
          this.popupService.showPopup({
            status: 'isSuccess',
            translationKey: 'translateModal.toast.success',
            isBackToLogin: false,
          });
        },
        error: () => {
          resultRef.close();
          this.dialogRef.close({ refresh: false });
          this.popupService.showPopup({
            status: 'failure',
            translationKey: 'translateModal.toast.failure',
            isBackToLogin: false,
          });
        }
      });
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonType = ButtonType;
  protected readonly ButtonState = ButtonState;
}
